import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import ConfirmDialog from './ConfirmDialog';
import { API } from '../App';
import { useT } from '../i18n';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Users, UserPlus, Trash2, Mail, ExternalLink, ShieldOff } from 'lucide-react';
import XpnsrPageHeader from './XpnsrPageHeader';

const ROLES = ['viewer', 'manager', 'admin'];

export default function Workspace() {
  const { t } = useT();
  const [members, setMembers] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [tokenToken, setTokenToken] = useState(null);
  const [inviting, setInviting] = useState(false);

  const load = async () => {
    try {
      const [m, me] = await Promise.allSettled([
        axios.get(`${API}/workspace/members`),
        axios.get(`${API}/workspace/memberships`),
      ]);
      if (m.status === 'fulfilled') setMembers(m.value.data || []);
      if (me.status === 'fulfilled') setMemberships(me.value.data || []);
    } catch (e) {
      console.error('Error loading workspace data:', e);
    }
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!email.trim() || inviting) return;
    setInviting(true);
    try {
      const r = await axios.post(`${API}/workspace/members`, { email: email.trim(), role });
      setEmail('');
      if (r.data.status === 'invited') {
        setTokenToken(r.data.invite_token);
      }
      toast.success(t('Invite sent'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Action failed'));
    } finally {
      setInviting(false);
    }
  };

  const [revokeTarget, setRevokeTarget] = useState(null);
  const [reset2faTarget, setReset2faTarget] = useState(null);

  const revoke = async (id) => {
    try {
      await axios.delete(`${API}/workspace/members/${id}`);
      setRevokeTarget(null);
      toast.success(t('Access revoked'));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Failed to revoke access'));
    }
  };

  const reset2fa = async (member) => {
    // Only fires for members who already accepted the invite — pending
    // invitees have no `member_user_id` yet so the action is hidden.
    if (!member.member_user_id) return;
    try {
      const r = await axios.post(`${API}/admin/users/${member.member_user_id}/2fa/reset`);
      setReset2faTarget(null);
      if (r.data?.was_enabled) {
        toast.success(t('2FA reset — member can re-enrol'));
      } else {
        toast.success(t('Member had no 2FA configured'));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('2FA reset failed'));
    }
  };

  const acceptUrl = tokenToken ? `${window.location.origin}/workspace/accept/${tokenToken}` : null;

  return (
    <div className="px-8 py-10 max-w-5xl">
      <XpnsrPageHeader
        kicker={t('Team')}
        title={t('Workspace')}
        subtitle={t('Invite teammates with roles. Owner sees everything; manager/admin can edit; viewer is read-only.')}
      />
      <div className="mb-6" />

      <Card className="mb-6">
        <CardHeader><CardTitle className="flex items-center gap-2"><UserPlus className="w-4 h-4" /> {t('Invite member')}</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <Label>{t('Email')}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@company.com" />
            </div>
            <div>
              <Label>{t('Role')}</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={invite} disabled={inviting}>{inviting ? t('Sending…') : t('Invite')}</Button>
          </div>
          {acceptUrl && (
            <div className="mt-4 p-3 rounded border border-primary/30 bg-primary/5 text-xs">
              <div className="font-mono text-muted-foreground mb-1">{t('Share this link with the invitee:')}</div>
              <div className="flex gap-2 items-center">
                <code className="flex-1 break-all">{acceptUrl}</code>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(acceptUrl); toast.success(t('Copied')); }}>{t('Copy')}</Button>
                <Button size="sm" variant="outline" onClick={() => setTokenToken(null)}>{t('Dismiss')}</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader><CardTitle className="flex items-center gap-2"><Users className="w-4 h-4" /> {t('Members')}</CardTitle></CardHeader>
        <CardContent>
          {members.length === 0 ? (
            <div className="text-center py-6">
              <Users className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-muted-foreground text-sm">{t('No members yet')}</p>
              <p className="text-muted-foreground text-xs mt-1">{t('Invite teammates above to collaborate.')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {members.map(m => (
                <div key={m.id} className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0">
                  <div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="font-medium">{m.email}</span>
                      <Badge variant="outline">{m.role}</Badge>
                      {m.status === 'active'
                        ? <Badge className="bg-primary/15 text-primary border border-primary/30">{t('Active')}</Badge>
                        : <Badge variant="outline">{t('Invited')}</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {m.status === 'active' && m.member_user_id && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setReset2faTarget(m)}
                        aria-label={t('Reset 2FA')}
                        title={t('Reset 2FA')}
                        data-testid={`reset-2fa-${m.id}`}
                      >
                        <ShieldOff className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRevokeTarget(m)}
                      aria-label={t('Revoke access')}
                      title={t('Revoke access')}
                      data-testid={`revoke-${m.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><ExternalLink className="w-4 h-4" /> {t('Shared with me')}</CardTitle></CardHeader>
        <CardContent>
          {memberships.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t('No shared workspaces')}</p>
          ) : (
            <div className="space-y-2">
              {memberships.map(m => (
                <div key={m.id} className="py-2 border-b border-border last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{m.owner?.full_name || m.owner?.email}</span>
                    <Badge variant="outline">{m.role}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">{m.owner?.email}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!revokeTarget}
        onOpenChange={(open) => { if (!open) setRevokeTarget(null); }}
        title={t('Revoke access')}
        description={t('This member will lose access to this workspace immediately.')}
        confirmLabel={t('Revoke')}
        onConfirm={() => revoke(revokeTarget?.id)}
      />
      <ConfirmDialog
        open={!!reset2faTarget}
        onOpenChange={(open) => { if (!open) setReset2faTarget(null); }}
        title={t('Reset 2FA')}
        description={t('This will reset two-factor authentication for this member. They will need to re-enrol.')}
        confirmLabel={t('Reset 2FA')}
        onConfirm={() => reset2fa(reset2faTarget)}
      />
    </div>
  );
}
